import React, { useState, type FunctionComponent } from "react";
import { Badge, Button, DatePicker, Descriptions, Form, Grid, message, Modal, Radio, Space, Tag } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { productClientActions } from "@fitness-app/app-store";
import { ProductDurationType } from "@fitness-app/data-models/entities/Product";
import { type FreeProductClient } from "@fitness-app/data-models/entities/ProductClient";

import { FREE_STATUS_MAPPER } from "~/modules/Products/constants/oneTimeStatusMapper";
import { paymentTypeColorMapper } from "~/modules/Products/constants/paymentTypeColorMapper";
import { useAppDispatch } from "~/store/initializeStore";
import { describeExtendAccessPeriodError } from "./extendAccessPeriodErrorMessage";

interface OwnProps {
  productClient: FreeProductClient;
  ownerView?: boolean;
}

type Props = OwnProps;

const DATE_FORMAT = "DD.MM.YYYY";

interface FormModel {
  resumes_at: null | Dayjs;
}

const EXTEND_SHORTCUTS = [
  { value: 1, unit: "week", label: "pauseCollection.extendAbout1Week" },
  { value: 2, unit: "week", label: "pauseCollection.extendAbout2Weeks" },
  { value: 1, unit: "month", label: "pauseCollection.extendAbout1Month" },
] as const;

const ProductClientFreeSettings: FunctionComponent<Props> = ({ productClient, ownerView }) => {
  const { t } = useTranslation(["client", "products", "common"]);
  const { lg } = Grid.useBreakpoint();
  const dispatch = useAppDispatch();
  const [formController] = Form.useForm<FormModel>();
  const [showModal, toggleModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only a time-boxed free product (a trial month, Klucz's 14 days) has an end to move.
  const duration = productClient.productDuration;
  const accessEnd =
    duration &&
    (duration.type === ProductDurationType.LastsFor || duration.type === ProductDurationType.UntilDate) &&
    duration.endAt
      ? dayjs.unix(duration.endAt)
      : null;

  const openModal = () => {
    // The form outlives the modal, so a date picked last time would still be there.
    formController.setFieldsValue({ resumes_at: null });
    toggleModal(true);
  };

  const handleFormFinish = async (formData: FormModel) => {
    setSaving(true);
    try {
      await dispatch(
        productClientActions.setExtendAccessPeriod({
          productId: productClient.productId,
          clientEmail: productClient.email,
          resume_at: formData.resumes_at ? formData.resumes_at.endOf("day").unix() : null,
        }),
      ).unwrap();
      toggleModal(false);
    } catch (e) {
      void message.error(describeExtendAccessPeriodError(e, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Descriptions bordered labelStyle={{ width: 250 }} layout={lg ? "horizontal" : "vertical"}>
        <Descriptions.Item label={t("subscription.status")} span={3}>
          <Badge
            status={FREE_STATUS_MAPPER[productClient.status]}
            text={t(`common:freeAccessStatus.${productClient.status}`)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t("list.columns.paymentType")} span={3}>
          <Tag color={paymentTypeColorMapper[productClient.paymentType]}>{t(`type.${productClient.paymentType}`)}</Tag>
        </Descriptions.Item>
        {accessEnd && duration?.startAt ? (
          <Descriptions.Item label={t("list.columns.accessPeriod")} span={3}>
            <Space direction="horizontal" size={8}>
              <span>
                {dayjs.unix(duration.startAt).format(DATE_FORMAT)} - {accessEnd.format(DATE_FORMAT)}
              </span>
              {ownerView && productClient.status === "active" ? (
                <Button type="link" onClick={openModal}>
                  {t("pauseCollection.changeAccessPeriod")}
                </Button>
              ) : null}
            </Space>
          </Descriptions.Item>
        ) : null}
      </Descriptions>
      {ownerView && accessEnd ? (
        <Modal
          open={showModal}
          onCancel={() => toggleModal(false)}
          title={t("pauseCollection.modalTitle")}
          width={600}
          okText={t("common:button.save")}
          onOk={() => formController.submit()}
          okButtonProps={{ loading: saving }}
        >
          <Form<FormModel>
            form={formController}
            labelCol={{ xxl: { span: 8, offset: 4 }, span: 24, offset: 0 }}
            wrapperCol={{ xxl: { span: 16, offset: 4 }, span: 24, offset: 0 }}
            onFinish={handleFormFinish}
          >
            <Form.Item label={t("pauseCollection.accessUntil")}>
              <DatePicker disabled value={accessEnd} style={{ minWidth: 250 }} />
            </Form.Item>
            <Form.Item
              name="resumes_at"
              label={t("pauseCollection.extendTo")}
              rules={[{ required: true, message: t("common:validationErrors.fieldIsRequired") }]}
            >
              <DatePicker
                style={{ minWidth: 250 }}
                disabledDate={(current) => current < accessEnd.endOf("day")}
                defaultPickerValue={accessEnd.add(1, "day")}
                showToday={false}
              />
            </Form.Item>
          </Form>
          <div style={{ textAlign: "center", marginTop: 10 }}>
            <Radio.Group value={null}>
              {EXTEND_SHORTCUTS.map((shortcut) => (
                <Radio.Button
                  key={shortcut.label}
                  value={shortcut.label}
                  onClick={() =>
                    formController.setFieldsValue({ resumes_at: accessEnd.add(shortcut.value, shortcut.unit) })
                  }
                >
                  {t(shortcut.label)}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default ProductClientFreeSettings;
